import Price from '@/presentation/components/elements/Price';
import ellipsis from '@/presentation/utils/ellipsis';
import { Vault } from 'nftx.js';
import { useFormatter, useTranslations } from 'use-intl';
import { Chain, formatEther } from 'viem';

const SelectedVault = ({
  chain,
  asset,
  token,
  totalHoldings,
  prices,
  fees,
}: Partial<Vault> & { chain: Chain }) => {
  const { number } = useFormatter();
  const t = useTranslations('CreatePosition.SelectedVault');

  return (
    <div className="overflow-hidden bg-gray-50 rounded-2xl border border-gray-300">
      <header className="p-4 bg-gray-100 font-bold border-gray-300 border-b">
        <h3>{t('title')}</h3>
      </header>
      <div className="p-4">
        <h4 className="text-sm mb-3 font-medium text-gray-800/50">
          {t('asset.title')}
        </h4>
        <dl className="space-y-3 text-sm mb-6">
          {(
            [
              { key: 'name', value: asset?.name },
              { key: 'address', value: asset?.id },
            ] as const
          ).map((x) => (
            <div className="flex justify-between space-x-2" key={x.key}>
              <dt className="font-medium text-gray-800/75">
                {t(`asset.${x.key}`)}
              </dt>
              <dd className="text-gray-500 text-right">
                {x.key === 'address' && x.value ? (
                  <a
                    href={`${chain.blockExplorers?.default.url}/address/${x.value}`}
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="underline hover:no-underline"
                  >
                    {ellipsis(x.value)}
                  </a>
                ) : (
                  x.value || '--'
                )}
              </dd>
            </div>
          ))}
        </dl>
        <h4 className="text-sm mb-3 font-medium text-gray-800/50">
          {t('token.title')}
        </h4>
        <dl className="space-y-3 text-sm mb-6">
          {(
            [
              { key: 'symbol', value: token?.symbol },
              { key: 'address', value: token?.id },
            ] as const
          ).map((x) => (
            <div className="flex justify-between space-x-2" key={x.key}>
              <dt className="font-medium text-gray-800/75">
                {t(`token.${x.key}`)}
              </dt>
              <dd className="text-gray-500 text-right">
                {x.key === 'address' && x.value ? (
                  <a
                    href={`${chain.blockExplorers?.default.url}/address/${x.value}`}
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="underline hover:no-underline"
                  >
                    {ellipsis(x.value)}
                  </a>
                ) : (
                  x.value || '--'
                )}
              </dd>
            </div>
          ))}
        </dl>
        <h4 className="text-sm mb-3 font-medium text-gray-800/50">
          {t('details.title')}
        </h4>
        <dl className="space-y-3 text-sm">
          {(
            [
              { key: 'totalHoldings', value: totalHoldings },
              {
                key: 'fee.title',
                value: (
                  <dl>
                    {[fees?.mintFee, fees?.redeemFee, fees?.swapFee].map(
                      (x, i) => (
                        <div key={i} className="flex justify-end space-x-2">
                          <dt>{t(`details.fee.${i as 0 | 1 | 2}`)}</dt>
                          <dd>
                            {number(Number(formatEther(x || 0n)), {
                              style: 'percent',
                              minimumSignificantDigits: 1,
                            })}
                          </dd>
                        </div>
                      )
                    )}
                  </dl>
                ),
              },
              {
                key: 'buyPrice',
                value: <Price value={prices?.[0]?.redeem.price} />,
              },
              {
                key: 'sellPrice',
                value: <Price value={prices?.[0]?.mint.price} />,
              },
            ] as const
          ).map((x) => (
            <div className="flex justify-between space-x-2" key={x.key}>
              <dt className="font-medium text-gray-800/75">
                {t(`details.${x.key}`)}
              </dt>
              <dd className="text-gray-500 text-right">{x.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
};

export default SelectedVault;
